import { useAsesoriasEnCurso } from '@/hooks';
import type { AsesoriaEnCurso } from '@/types/types';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ColumnPicker, useColumnVisibility, type ColDef } from '@/components/ui/ColumnPicker';

// ─── Helpers ────────────────────────────────────────

function formatHours(h: number): string {
  const t = Math.round(h * 3600);
  const hrs = Math.floor(t / 3600);
  const min = Math.floor((t % 3600) / 60);
  if (hrs === 0) return `${min}m`;
  return `${hrs}h ${min}m`;
}

function ProgBar({ value, color }: { value: number; color: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="admin-prog-bar">
        <span className="admin-prog-fill" style={{ width: `${Math.min(value, 100)}%`, background: color }} />
      </span>
      <span className="text-[12px] font-medium">{value.toFixed(0)}%</span>
    </span>
  );
}

function pColor(v: number): string {
  if (v >= 70) return 'var(--admin-green)';
  if (v >= 50) return '#e09b00';
  return 'var(--admin-red)';
}

// ─── Column definitions ──────────────────────────────

type ColKey = 'empresa' | 'asesor' | 'ntc' | 'horas' | 'acumulado' | 'objetivo' | 'cumplimiento_inicial' | 'cumplimiento' | 'cambio';

const COLS: ColDef<ColKey>[] = [
  { key: 'empresa', label: 'Empresa', defaultVisible: true },
  { key: 'asesor', label: 'Asesor', defaultVisible: true },
  { key: 'ntc', label: 'NTC', defaultVisible: true },
  { key: 'horas', label: 'Horas', defaultVisible: true },
  { key: 'acumulado', label: 'Acumulado', defaultVisible: false },
  { key: 'cumplimiento_inicial', label: 'Cumpl. inicial', defaultVisible: false },
  { key: 'cumplimiento', label: 'Cumpl. actual', defaultVisible: true },
  { key: 'cambio', label: 'Cambio', defaultVisible: true },
  { key: 'objetivo', label: 'Objetivo', defaultVisible: true },
];

// ─── Main Page ─────────────────────────────────────

export function AdminEnCursoPage() {
  const navigate = useNavigate();
  const { asesorias, isLoading, error } = useAsesoriasEnCurso();

  const { visible, toggle, reset, activeCount } = useColumnVisibility<ColKey>(COLS, 'tourixia:admin-encurso-cols');

  const uniqueAsesores = useMemo(() => new Set(asesorias.map((a: AsesoriaEnCurso) => a.asesor.asesorId)).size, [asesorias]);

  if (isLoading) {
    return (
      <div className="p-12 text-center">
        <div className="w-6 h-6 border-2 border-(--admin-blue) border-t-transparent rounded-full animate-spin mx-auto" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-12 text-center text-(--admin-red)">
        <p className="font-medium">{error}</p>
      </div>
    );
  }

  const activeCols = COLS.filter((c) => visible[c.key]);

  return (
    <>
      {/* Summary */}
      <div className="admin-metrics-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <div className="admin-metric-card">
          <div className="text-[11px] text-(--admin-text3) font-medium uppercase tracking-wider mb-1.5">Asesorías en curso</div>
          <div className="text-[26px] font-semibold tracking-tight text-(--admin-green)">{asesorias.length}</div>
          <div className="text-[11px] text-(--admin-text3) mt-1">Actualmente activas</div>
        </div>
        <div className="admin-metric-card">
          <div className="text-[11px] text-(--admin-text3) font-medium uppercase tracking-wider mb-1.5">Asesores activos</div>
          <div className="text-[26px] font-semibold tracking-tight">{uniqueAsesores}</div>
          <div className="text-[11px] text-(--admin-text3) mt-1">Con asesorías en curso</div>
        </div>
        <div className="admin-metric-card">
          <div className="text-[11px] text-(--admin-text3) font-medium uppercase tracking-wider mb-1.5">Horas acumuladas</div>
          <div className="text-[26px] font-semibold tracking-tight">
            {formatHours(asesorias.reduce((s: number, a: AsesoriaEnCurso) => s + (a.horas ?? 0), 0))}
          </div>
          <div className="text-[11px] text-(--admin-text3) mt-1">En ejecución activa</div>
        </div>
      </div>

      {/* Table */}
      <div className="admin-card">
        <div className="admin-card-header flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="text-[13px] font-semibold text-(--admin-text)">Asesorías activas</div>
            <span className="admin-chip bg-(--admin-green-bg) text-(--admin-green)">{asesorias.length}</span>
          </div>
          <ColumnPicker cols={COLS} visible={visible} onToggle={toggle} onReset={reset} activeCount={activeCount} />
        </div>
        <div className="overflow-x-auto">
          <table className="admin-table">
            <thead>
              <tr>
                {activeCols.map((c) => (
                  <th key={c.key}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {asesorias.map((a) => {
                const cumplInicial = a.porcentajeCumplimiento ?? 0;
                const cumplActual = a.porcentajeCumplimientoActual ?? 0;
                const cambio = a.cambioCumplimiento ?? 0;
                return (
                  <tr
                    key={a.asesoriaId}
                    className="clickable"
                    onClick={() => navigate(`/empresas/${a.empresa.nitId}/asesorias/${a.asesoriaId}`)}
                  >
                    {visible.empresa && (
                      <td className="whitespace-nowrap">
                        <div className="font-medium text-(--admin-text) max-w-48 truncate">{a.empresa.nombre}</div>
                        <div className="font-mono text-[10px] text-(--admin-text3)">{a.empresa.nitId}</div>
                      </td>
                    )}
                    {visible.asesor && (
                      <td className="whitespace-nowrap">
                        <div className="text-(--admin-text) font-medium">{a.asesor.nombre}</div>
                      </td>
                    )}
                    {visible.ntc && (
                      <td className="whitespace-nowrap">
                        <span
                          className="admin-chip bg-(--admin-blue-bg) text-(--admin-blue) text-[10px] max-w-72 truncate block"
                          title={a.combinacionNtc?.nombre ?? '—'}
                        >
                          {a.combinacionNtc?.nombre ?? '—'}
                        </span>
                      </td>
                    )}
                    {visible.horas && (
                      <td className="text-(--admin-text2) font-medium whitespace-nowrap">
                        {formatHours(a.horas)} (+{((a.horas / 320) * 100).toFixed(1)}%)
                      </td>
                    )}
                    {visible.acumulado && (
                      <td className="text-(--admin-text3) font-medium whitespace-nowrap">{formatHours(a.empresa.horasTotales)}</td>
                    )}
                    {visible.cumplimiento_inicial && (
                      <td className="whitespace-nowrap">
                        <ProgBar value={cumplInicial} color={pColor(cumplInicial)} />
                      </td>
                    )}
                    {visible.cumplimiento && (
                      <td className="whitespace-nowrap">
                        <ProgBar value={cumplActual} color={pColor(cumplActual)} />
                      </td>
                    )}
                    {visible.cambio && (
                      <td className="whitespace-nowrap text-center">
                        <span
                          className={`text-[12px] font-bold ${cambio > 0 ? 'text-(--admin-green)' : cambio < 0 ? 'text-(--admin-red)' : 'text-(--admin-text3)'}`}
                        >
                          {cambio > 0 ? `+${cambio.toFixed(1)}%` : `${cambio.toFixed(1)}%`}
                        </span>
                      </td>
                    )}
                    {visible.objetivo && (
                      <td className="text-(--admin-text3) text-[11px] max-w-xs truncate whitespace-nowrap">{a.objetivo}</td>
                    )}
                  </tr>
                );
              })}
              {asesorias.length === 0 && (
                <tr>
                  <td colSpan={activeCols.length} className="text-center py-10 text-(--admin-text3)">
                    <div className="text-[20px] mb-2">✓</div>
                    No hay asesorías en curso actualmente
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
